import * as Sentry from '@sentry/react';
import ButtonComponent from 'core/modules/atoms/Button';
import IconComponent from 'core/modules/atoms/Icon';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

// Type assertions for JS components without TypeScript definitions
const Button = ButtonComponent as React.ComponentType<{
  text?: string;
  color?: string;
  size?: string;
  onClick?: () => void;
}>;

const Icon = IconComponent as React.ComponentType<{
  type?: string;
  size?: string;
  color?: string;
}>;

interface Props extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

function isChunkLoadError(error: Error): boolean {
  const message = error?.message?.toLowerCase() ?? '';
  return (
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    error.name === 'ChunkLoadError'
  );
}

/**
 * Error boundary that catches chunk loading failures (CSS/JS) after
 * @rsbuild/plugin-assets-retry has exhausted its network-level retries.
 * Provides a simple "Reload page" fallback as a last resort.
 */
class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'chunk_load_error');
      scope.setTag('route', window.location.pathname);
      scope.setExtra('componentStack', errorInfo.componentStack);
      scope.setExtra('url', window.location.href);
      scope.setExtra('userAgent', navigator.userAgent);
      scope.setLevel('error');
      Sentry.captureException(error);
    });

    console.error('[ChunkLoadErrorBoundary] Chunk load error:', {
      message: error.message,
      route: window.location.pathname,
      stack: errorInfo.componentStack,
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError } = this.state;
    const { children, fallback, t } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <div className="chunk-error">
          <div className="chunk-error__content">
            <div className="chunk-error__icon">
              <Icon type="warning-circle-fill" size="xxl" color="primary" />
            </div>

            <h2 className="chunk-error__title">{t('error:Error loading page')}</h2>

            <p className="chunk-error__message">{t('error:Page load error message')}</p>

            <div className="chunk-error__actions">
              <Button text={t('error:Reload page')} color="primary" size="medium" onClick={this.handleReload} />
            </div>
          </div>
        </div>
      );
    }

    return <>{children}</>;
  }
}

export default withTranslation()(ChunkLoadErrorBoundary);
